import { allSuburbProfiles } from './suburbProfilesData';

/**
 * Cluster 5: "How the 2032 Olympics Could Affect Property in [Suburb]"
 * Template-generated for all 119 SEQ suburbs.
 * Each article focuses on suburb-specific Olympic impact.
 */

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const images = [
  img('1569517282132-25f81e48c24a'), img('1461896836934-ffe607ba8211'),
  img('1535131749002-5830f3465147'), img('1587385789097-0197a7fbd179'),
  img('1600585154340-be6161a56a0c'), img('1590674899484-d5640e854abe'),
  img('1600607687939-ce8a6c25118c'), img('1564013799-e0cbc9f31999'),
];

const olympicVenues = [
  { name: 'The Gabba', suburb: 'Woolloongabba', type: 'Main Stadium (Athletics, Ceremonies)', distance: (s) => getDistance(s, -27.4858, 153.0381) },
  { name: 'Brisbane Arena', suburb: 'Roma Street', type: 'Basketball, Gymnastics', distance: (s) => getDistance(s, -27.4650, 153.0180) },
  { name: 'Suncorp Stadium', suburb: 'Milton', type: 'Football, Rugby', distance: (s) => getDistance(s, -27.4648, 153.0094) },
  { name: 'Brisbane Entertainment Centre', suburb: 'Boondall', type: 'Boxing, Table Tennis', distance: (s) => getDistance(s, -27.3413, 153.0686) },
  { name: 'South Bank Aquatic Centre', suburb: 'South Brisbane', type: 'Swimming, Diving', distance: (s) => getDistance(s, -27.4784, 153.0200) },
  { name: 'Chandler Velodrome', suburb: 'Chandler', type: 'Track Cycling', distance: (s) => getDistance(s, -27.5174, 153.1638) },
  { name: 'Anna Meares Velodrome', suburb: 'Chandler', type: 'Cycling', distance: (s) => getDistance(s, -27.5174, 153.1638) },
  { name: 'Robina Stadium', suburb: 'Robina', type: 'Football', distance: (s) => getDistance(s, -28.0774, 153.3870) },
  { name: 'Sunshine Coast Stadium', suburb: 'Kawana', type: 'Football', distance: (s) => getDistance(s, -26.7246, 153.1177) },
];

function getDistance(suburb, vLat, vLng) {
  // Approximate distances based on suburb regions
  return null; // Template uses suburb.distanceFromCBD instead
}

const getClosestVenues = (profile) => {
  const dist = profile.distanceFromCBD || '';
  const km = parseFloat(dist) || 10;
  // Assign venues based on proximity logic
  const venues = [];
  if (km <= 5) {
    venues.push('The Gabba (Main Stadium)', 'Brisbane Arena (Roma Street)', 'Suncorp Stadium', 'South Bank Aquatic Centre');
  } else if (km <= 15) {
    venues.push('The Gabba', 'Brisbane Arena', 'Suncorp Stadium');
  } else if (km <= 30) {
    venues.push('Brisbane Entertainment Centre', 'The Gabba');
  }
  if (profile.councilSlug === 'gold-coast') {
    venues.push('Robina Stadium', 'Broadbeach Events Hub', 'Carrara Stadium');
  }
  if (profile.councilSlug === 'sunshine-coast') {
    venues.push('Sunshine Coast Stadium (Kawana)');
  }
  if (venues.length === 0) venues.push('Multiple Brisbane venues');
  return venues.slice(0, 4);
};

const createOlympicsArticle = (profile, idx) => {
  const { name, slug, council, distanceFromCBD, medianHouse, growthDrivers, infrastructure, developmentPotential } = profile;
  const venues = getClosestVenues(profile);
  const drivers = (growthDrivers || []).slice(0, 3);
  const infra = (infrastructure || []).slice(0, 4);

  return {
    id: `brisbane-2032-olympics-impact-${slug}-property`,
    category: 'Market Analysis',
    title: `How the 2032 Olympics Could Affect Property in ${name}`,
    excerpt: `The Brisbane 2032 Olympics is reshaping South East Queensland. Here's what it means for property owners and values in ${name}.`,
    date: '2026-04-09',
    readTime: '8 min read',
    image: images[idx % images.length],
    metaDescription: `How will the Brisbane 2032 Olympics affect property values in ${name}? Analysis of infrastructure, development, and opportunities for ${name} property owners.`,
    suburbs: [name],
    content: `
## How the 2032 Olympics Could Affect Property in ${name}

The Brisbane 2032 Olympic and Paralympic Games represent the largest infrastructure investment in South East Queensland's history. With billions allocated to transport, venues, and urban renewal, the Games are already reshaping property markets across the region — and ${name} is positioned to benefit.

## ${name}'s Olympic Proximity

${name} is located ${distanceFromCBD} from the Brisbane CBD. The closest Olympic venues include:

${venues.map(v => `- **${v}**`).join('\n')}

Historical data from Sydney 2000, London 2012, and Tokyo 2020 shows that suburbs within 30 minutes of Olympic venues experience sustained property value growth of **10–25% above surrounding areas** in the decade following the Games.

## Infrastructure Investment Affecting ${name}

The Olympics has accelerated infrastructure projects that directly benefit ${name}:

${infra.map(i => `- ${i}`).join('\n')}

### Transport Upgrades
Cross River Rail, Brisbane Metro, and road network upgrades are improving connectivity across South East Queensland. For ${name}, this means:

- Reduced commute times to the CBD and key employment centres
- Better public transport frequency and reliability
- Improved road access and intersection upgrades
- Cycling and pedestrian infrastructure improvements

These transport improvements permanently increase an area's desirability — and property values follow.

### Urban Renewal and Amenity
Olympics host cities consistently upgrade public spaces, parks, and community facilities. South East Queensland is investing in:

- New parklands and green spaces near venues
- Upgraded community sporting facilities
- Improved streetscapes and public domains
- New dining and entertainment precincts

## How This Affects ${name} Property Values

### Short-Term Impact (2026–2032)
The lead-up to the Games typically sees:

- **Increased developer activity** as builders race to complete projects before 2032
- **Rising demand for development sites** in well-located suburbs
- **Infrastructure premium** as transport projects near completion
- **Rental demand growth** from construction workers and Games preparation staff

### Games Period Impact (2032)
During the Games:

- **Short-term rental surge** — Airbnb and holiday rental rates increase significantly
- **International exposure** — global spotlight on Brisbane and SEQ lifestyle
- **Tourist spillover** — visitors discover suburbs beyond the CBD

### Long-Term Legacy (2032+)
The lasting impact is the most significant:

- **Permanent infrastructure** — transport, venues, and amenity remain
- **Population growth acceleration** — Olympics showcase attracts permanent migration
- **Sustained development** — ongoing urban renewal extends beyond Games requirements
- **International investment** — global capital follows Olympic exposure

Sydney's Olympic suburbs (Homebush, Newington, Rhodes) saw property values **triple** in the 15 years following the 2000 Games. Similar patterns are expected across SEQ.

## What This Means for ${name} Property Owners

${developmentPotential ? `${name}'s development outlook: ${developmentPotential.slice(0, 200)}.` : `${name} is positioned to benefit from the Olympic development wave.`}

If you own property in ${name}, the Olympic effect means:

1. **Your property may have development potential** that didn't exist 5 years ago. Rezoning and infrastructure changes are opening new opportunities.
2. **Developer demand is increasing.** Builders want to complete projects before 2032, creating urgency in the acquisition market.
3. **Timing matters.** The strongest seller's market typically occurs 3–5 years before the Games, as developers compete for remaining sites.

> The window for selling development sites at peak Olympic premium is **now through 2029**. After that, the most desirable sites will already be acquired.

## Is Your ${name} Property a Potential Development Site?

Not every property in ${name} will benefit equally. The Olympic premium is strongest for:

- Properties zoned for medium-to-high density development
- Sites near transport infrastructure (current or planned)
- Larger blocks (600m²+) suitable for multi-dwelling projects
- Corner blocks and sites with dual street access
- Properties in designated growth corridors

ACRES provides complimentary development site assessments for ${name} property owners. We'll assess your zoning, constraints, and proximity to Olympic infrastructure to determine your property's potential.

**Find out if your ${name} property is positioned to benefit from the 2032 Olympics.**

## Frequently Asked Questions

**Q: Will the Olympics definitely increase property values in ${name}?**

A: While no guarantee exists for individual properties, the historical pattern across every modern Olympic host city shows sustained property growth in well-located suburbs. ${name}'s position and infrastructure improvements make it well-placed.

**Q: When is the best time to sell in ${name} before the Olympics?**

A: The peak development site premium typically occurs 3–5 years before the Games (2027–2029), when developer acquisition activity is most intense. However, early movers often achieve strong prices with less competition.

**Q: Do I need to wait for the Olympics to sell my property?**

A: No. Developer demand is already elevated due to Olympic infrastructure investment. Selling now may actually achieve a better result than waiting, as developer acquisition budgets become more competitive over time.
`,
    faq: [
      { q: `Will the Olympics increase property values in ${name}?`, a: `Historical data shows suburbs near Olympic venues experience 10–25% above-average growth. ${name}'s infrastructure improvements make it well-placed.` },
      { q: `When is the best time to sell in ${name} before the Olympics?`, a: `Peak development site premium is typically 3–5 years before the Games (2027–2029). However, early movers often achieve strong prices.` },
      { q: `Do I need to wait for the Olympics to sell?`, a: `No. Developer demand is already elevated. Selling now may achieve better results as competition increases.` },
    ],
  };
};

export const olympicsImpactArticles = allSuburbProfiles.map((p, i) => createOlympicsArticle(p, i));
